import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/atlas/Shared";
import { Field, GoldButton, GhostButton, NativeSelect } from "@/components/atlas/Fields";
import { PositionsEditor } from "@/components/events/PositionsEditor";
import { GeoFields } from "@/components/events/GeoFields";
import { api } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";

const BLANK = {
  name: "", client: "", date: "", location: "", address: "", start_time: "18:00", end_time: "23:00", report_time: "17:00",
  description: "", dress_code: "", instructions: "", setup_instructions: "", teardown_instructions: "", equipment: "",
  client_instructions: "", supervisor_name: "", supervisor_phone: "", internal_notes: "", availability_deadline: "",
  attachments: [], positions: [], address2: "", geo_lat: null, geo_lng: null, geo2_lat: null, geo2_lng: null,
};

const Section = ({ title, children, dark }) => (
  <section className={`${dark ? "dark-zone bg-[#0A0A0C] text-white border border-black" : "card-lux"} p-6 md:p-8 rise`}>
    <h2 className={`font-display text-2xl mb-6 ${dark ? "text-[#C5A059]" : ""}`}>{title}</h2>
    {children}
  </section>
);

export default function EventForm() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) api.get(`/events/${id}`).then((r) => setForm({ ...BLANK, ...r.data }));
    else api.get("/templates").then((r) => setTemplates(r.data));
  }, [id]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const applyTemplate = (tid) => {
    const tpl = templates.find((x) => x.id === tid);
    if (tpl) setForm((p) => ({ ...p, ...tpl.data, positions: tpl.data.positions.map((s) => ({ ...s })) }));
  };
  const setAttachment = (i, k, v) => setForm((p) => ({ ...p, attachments: p.attachments.map((a, j) => (j === i ? { ...a, [k]: v } : a)) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.positions.length) return toast.error(t("f.noPositions"));
    setBusy(true);
    try {
      const payload = { ...form, attachments: form.attachments.filter((a) => a.url) };
      const r = id ? await api.put(`/events/${id}`, payload) : await api.post("/events", payload);
      toast.success(t("c.saved"));
      navigate(`/admin/events/${r.data.id}`);
    } catch (err) {
      toast.error(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  const text = (k, label, opts = {}) => (
    <Field label={label} className={opts.className}>
      {opts.area
        ? <Textarea data-testid={`event-${k}-input`} value={form[k]} onChange={set(k)} rows={3} className="rounded-none bg-white text-black" />
        : <Input data-testid={`event-${k}-input`} type={opts.type || "text"} required={opts.required} value={form[k]} onChange={set(k)} className="h-10 rounded-none bg-white" />}
    </Field>
  );

  return (
    <form onSubmit={submit} className="max-w-5xl" data-testid="event-form">
      <PageHeader eyebrow={t("nav.events")} title={id ? t("f.editTitle") : t("f.newTitle")} actions={!id && templates.length > 0 && (
        <div className="w-64">
          <NativeSelect testid="event-template-select" value="" onChange={applyTemplate}>
            <option value="">{t("f.template")}…</option>
            {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
          </NativeSelect>
        </div>
      )} />
      <div className="space-y-6">
        <Section title={t("f.general")}>
          <div className="grid md:grid-cols-2 gap-5">
            {text("name", t("f.name"), { required: true, className: "md:col-span-2" })}
            {text("client", t("f.client"))}
            {text("date", t("f.date"), { type: "date", required: true })}
            {text("location", t("f.location"))}
            {text("address", t("f.address"))}
            {text("availability_deadline", t("f.deadline"), { type: "date" })}
            <div />
            {text("description", t("f.description"), { area: true, className: "md:col-span-2" })}
          </div>
        </Section>
        <Section title={t("t.gpsTitle")}>
          <GeoFields form={form} setForm={setForm} />
        </Section>
        <Section title={t("f.positions")}>
          <PositionsEditor value={form.positions} onChange={set("positions")} />
        </Section>
        <Section title={t("f.instructions")}>
          <div className="grid md:grid-cols-2 gap-5">
            {text("dress_code", t("f.dress"), { area: true })}
            {text("instructions", t("f.ops"), { area: true })}
            {text("setup_instructions", t("f.setup"), { area: true })}
            {text("teardown_instructions", t("f.teardown"), { area: true })}
            {text("equipment", t("f.equipment"), { area: true })}
            {text("client_instructions", t("f.clientInstr"), { area: true })}
            {text("supervisor_name", t("f.supervisor"))}
            {text("supervisor_phone", t("f.supervisorPhone"))}
          </div>
          <div className="mt-6">
            <Field label={t("f.attachments")}>
              <div className="space-y-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_40px] gap-2">
                    <Input data-testid={`attachment-label-${i}`} value={a.label} onChange={(e) => setAttachment(i, "label", e.target.value)} placeholder={t("f.label")} className="h-10 rounded-none" />
                    <Input data-testid={`attachment-url-${i}`} type="url" value={a.url} onChange={(e) => setAttachment(i, "url", e.target.value)} placeholder={t("f.url")} className="h-10 rounded-none" />
                    <button type="button" onClick={() => setForm((p) => ({ ...p, attachments: p.attachments.filter((_, j) => j !== i) }))} className="text-neutral-400 hover:text-red-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <GhostButton type="button" data-testid="add-attachment-btn" onClick={() => setForm((p) => ({ ...p, attachments: [...p.attachments, { label: "", url: "" }] }))}><Plus className="w-4 h-4" />{t("c.add")}</GhostButton>
              </div>
            </Field>
          </div>
        </Section>
        <Section title={<span className="flex items-center gap-2"><Lock className="w-4 h-4" />{t("f.internal")}</span>} dark>
          <Textarea data-testid="event-internal_notes-input" value={form.internal_notes} onChange={set("internal_notes")} rows={3} className="rounded-none bg-white/5 border-white/15 text-white" />
          <p className="text-xs text-white/50 mt-2">{t("f.internalHint")}</p>
        </Section>
        <div className="flex gap-3 justify-end pb-10">
          <GhostButton type="button" data-testid="event-form-cancel" onClick={() => navigate(-1)}>{t("c.cancel")}</GhostButton>
          <GoldButton data-testid="event-form-submit" disabled={busy}>{id ? t("c.save") : t("f.create")}</GoldButton>
        </div>
      </div>
    </form>
  );
}
